/**
 * Digital survey page
 */

// React
import React, { FC } from 'react';

// Material
import { Table, TableBody, TableCell, TableRow, Box, IconButton } from '@mui/material';

// Components
import SelectBase from 'components/Select';
import TableHeader from 'components/TableHeader';
import SearchInput from 'components/SearchInput';
import SelectInput from 'components/SelectInput';
import TotalRecords from 'components/TotalRecords';
import ProcessButton from 'components/ProcessButton';
import PaginationComponent from 'components/Pagination';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';

// Data mock
import {
  defaultParamsDigitalSurvey,
  headerDigitalSurvey,
  menuItems,
  systemUsageDigitalStatus,
} from 'initDataPages';

// Images
import { remove_icon } from 'assets/icons';

// Common styles
import {
  BpIcon,
  PageContainer,
  BpCheckedIcon,
  CustomTableHead,
  CustomTableContainer,
  WrapperSearchAndButton,
  BoxNoDataList,
  RecordsContainer,
  WrapperSearchAndBtnProcess,
} from 'styles';
import styled from '@emotion/styled';

// Utils
import { DataDigitalSurvey } from 'utils/types';
import { NOTIFICATION_MESSAGES, SYSTEM_MENU, VALUE_MENU } from 'utils/constants';
import {
  formatDate,
  formatNumberUs,
  formatSquareMeter,
  formatCurrencyUnit,
} from 'utils/helper/common';

// Hooks
import useGetListData from 'hooks/useGetListData';

// Services
import DigitalService from 'services/DigitalService';

// Component
const DigitalSurvey: FC = () => {
  const {
    state,
    perPage,
    status,
    dataList,
    isLoading,
    totalPages,
    currentPage,
    totalRecords,
    paramsGetList,
    scrollContainerRef,
    getIndex,
    isSortable,
    handleClose,
    handleRemove,
    handleExport,
    handleChangePage,
    handleRequestSort,
    handleSelectStatus,
    handleConfirmDelete,
  } = useGetListData<DataDigitalSurvey>(
    (paramsGetList) => DigitalService.getDigitalSurvey(paramsGetList),
    (paramsGetList) => DigitalService.exportDigitalSurvey(paramsGetList),
    (state) => DigitalService.deleteDigitalSurvey({ id: state.id }),
    defaultParamsDigitalSurvey,
    SYSTEM_MENU.DIGITAL_SURVEY,
    NOTIFICATION_MESSAGES.ERROR.DOWNLOAD
  );

  const WrapperTitle = styled(Box)({
    borderBottom: '2px solid #ddd',
    width: 'fit-content',
    padding: '4px',
    '&:last-child': {
      borderBottom: 'none',
    },
  });

  const renderTableCell = (arr: any, field: string) => {
    if (!arr || arr.length === 0) return null;

    const hasNonZeroValue = arr.some((item: any) => +item[field] > 0);

    return hasNonZeroValue
      ? arr.map((item: any) => (
          <WrapperTitle className="title-item" key={item.id}>
            {formatNumberUs(item[field] ?? 0)}
          </WrapperTitle>
        ))
      : null;
  };

  return (
    <PageContainer>
      <ConfirmDeleteModal
        open={state.openRemove ?? false}
        messages="この項目を削除して宜しいですか？"
        handleClose={handleClose}
        handleConfirmDelete={handleConfirmDelete}
      />

      <SelectInput menuItems={menuItems} defaultValue={VALUE_MENU.DIGITAL_SURVEY} />

      <RecordsContainer type="primary">
        <TotalRecords
          recordsPerPage={perPage}
          currentPage={currentPage}
          totalRecords={totalRecords}
        />
        <WrapperSearchAndButton type="primary">
          <SelectBase
            value={status}
            onChange={handleSelectStatus}
            menuItems={systemUsageDigitalStatus}
            sx={{ width: 145, mr: '26px' }}
            sxSelect={{ height: 37, fontSize: '16px' }}
            sxSelectItem={{ fontSize: '14px', padding: '10px' }}
          />
          <WrapperSearchAndBtnProcess>
            <SearchInput />
            <ProcessButton disabled={!totalRecords} handleDownload={handleExport} />
          </WrapperSearchAndBtnProcess>
        </WrapperSearchAndButton>
      </RecordsContainer>

      <CustomTableContainer>
        <Box className="table-container" ref={scrollContainerRef}>
          <Table aria-label="register user table">
            <CustomTableHead>
              <TableHeader
                isSortable={isSortable}
                header={headerDigitalSurvey}
                paramsGetList={paramsGetList}
                handleRequestSort={handleRequestSort}
              />
            </CustomTableHead>
            <TableBody>
              {dataList && dataList?.length > 0
                ? dataList?.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">{getIndex(index)}</TableCell>
                      <TableCell align="center">
                        {row?.status === 'in-used' ? <BpCheckedIcon /> : <BpIcon />}
                      </TableCell>

                      <TableCell align="left">{row?.user_company ?? ''}</TableCell>
                      <TableCell align="left">{row?.user_department ?? ''}</TableCell>
                      <TableCell align="left">{row?.user_name ?? ''}</TableCell>
                      <TableCell align="left">{row?.user_email ?? ''}</TableCell>
                      <TableCell align="left">{row?.title ?? ''}</TableCell>
                      <TableCell align="left">
                        {row?.questionnaires && row?.questionnaires.length > 0
                          ? row.questionnaires.map((questionnaire, index) => (
                              <WrapperTitle className="title-item" key={index}>
                                {questionnaire?.title ?? ''}
                              </WrapperTitle>
                            ))
                          : ''}
                      </TableCell>
                      <TableCell align="left">{formatDate(row?.start_date) ?? ''}</TableCell>
                      <TableCell align="left">{formatDate(row?.end_date) ?? ''}</TableCell>
                      <TableCell align="left">{row?.paper_questionnaire_answer_id ?? ''}</TableCell>
                      <TableCell align="left">{formatSquareMeter(row?.meter_square_num)}</TableCell>
                      <TableCell align="left">{formatCurrencyUnit(row?.exhibit_cost)}</TableCell>
                      <TableCell align="left">{formatCurrencyUnit(row?.decoration_cost)}</TableCell>
                      <TableCell align="left">{formatCurrencyUnit(row?.operation_cost)}</TableCell>
                      <TableCell align="left">{formatNumberUs(row?.comment_cost ?? '')}</TableCell>
                      <TableCell align="left">
                        {row?.cost_per_lead ? formatCurrencyUnit(row.cost_per_lead) : null}
                      </TableCell>
                      <TableCell align="left">
                        {renderTableCell(row?.questionnaires, 'answers_count')}
                      </TableCell>
                      <TableCell align="left">
                        {renderTableCell(row?.questionnaires, 'number_of_business_cards')}
                      </TableCell>
                      <TableCell align="left">
                        {renderTableCell(row?.questionnaires, 'number_of_questionnaires')}
                      </TableCell>
                      <TableCell align="center" onClick={() => handleRemove(row?.id)}>
                        <IconButton>
                          <img
                            src={remove_icon}
                            alt="remove"
                            style={{ width: '25px', height: 'auto', cursor: 'pointer' }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                : null}
            </TableBody>
          </Table>
          {dataList?.length === 0 && !isLoading && (
            <BoxNoDataList>{NOTIFICATION_MESSAGES.ERROR.SEARCH}</BoxNoDataList>
          )}
        </Box>
      </CustomTableContainer>

      <PaginationComponent
        totalPages={totalPages}
        currentPage={currentPage}
        dataLength={totalRecords}
        handleChangePage={handleChangePage}
      />
    </PageContainer>
  );
};

export default DigitalSurvey;
